<template>
  <div class="chart-container">
    <div v-if="String(type) === '1'" class="table-wrapper">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="(head, idx) in headers" :key="idx">{{ head }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ridx) in chartData" :key="ridx">
            <td v-for="(cell, cidx) in row" :key="cidx">
              {{ cell === null || cell === '' ? '-' : cell }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="['2', '3', '4'].includes(String(type))" class="echarts-container-box">
      <div ref="chartRef" class="echarts-wrapper"></div>
    </div>

    <div v-else-if="String(type) === '6'" v-html="chartData" class="html-content"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  type: { type: [String, Number], required: true },
  chartData: { type: [Array, Object, String], default: () => [] },
  field: { type: [Array, String], default: () => [] },
  title: {
    type: String,
    default: ''
  },
  subTitle: {
    type: String,
    default: ''
  }
})

const chartRef = ref(null)
let myChart = null
let resizeObserver = null

/* Glacier Light 专用调色盘（统一蓝系） */
const colors = [
  '#2563EB', // 主业务蓝（最大占比）
  '#38BDF8', // 数据蓝
  '#6366F1', // 结构蓝紫
  '#10B981', // 正向结果（利润 / 稳定）
  '#F59E0B', // 对比强调（高贡献）
  '#94A3B8'  // 其他 / 小项
]

const headers = computed(() => {
  if (!props.field) return []
  if (Array.isArray(props.field)) return props.field
  if (typeof props.field === 'string') {
    return props.field.replace(/[{}]/g, '').split(',').map(s => s.trim())
  }
  return []
})

const formatLongText = v =>
  typeof v === 'string' && v.length > 10 ? v.slice(0, 9) + '…' : v

/* 图表通用基础配置 */
const baseConfig = {
  backgroundColor: '#F8FAFC', // 图表内浅背景
  grid: { top: 70, left: 30, right: 30, bottom: 50, containLabel: true },
  title: {
    left: 12,
    top: 10,
    textStyle: {
      fontSize: 14,
      fontWeight: 600,
      color: '#1E293B'
    },
    subtextStyle: {
      fontSize: 12,
      color: '#64748B'
    }
  },
  tooltip: {
    trigger: 'axis',
    backgroundColor: '#FFFFFF',
    borderColor: '#E2E8F0',
    borderWidth: 1,
    padding: [10, 14],
    textStyle: { color: '#1E293B', fontSize: 13 },
    extraCssText:
      'box-shadow:0 8px 24px rgba(0,0,0,.08);border-radius:10px'
  },
  legend: {
    bottom: 6,
    textStyle: { color: '#64748B', fontSize: 12 },
    inactiveColor: '#CBD5E1'
  }
}

const getOption = () => {
  if (!props.chartData || Object.keys(props.chartData).length === 0) return null

  const xData = Object.keys(props.chartData)
  const yData = Object.values(props.chartData)

  /* 折线图 */
  if (String(props.type) === '2') {
    return {
      ...baseConfig,
      title: {
        ...baseConfig.title,
        text: props.title,
        subtext: props.subTitle
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        axisLabel: { color: '#64748B', rotate: 30, formatter: formatLongText }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#94A3B8' },
        splitLine: {
          lineStyle: { color: '#E5EDFF', type: 'dashed' }
        }
      },
      series: [
        {
          type: 'line',
          data: yData,
          smooth: true,
          symbolSize: 7,
          lineStyle: {
            width: 3,
            color: '#0066FF'
          },
          itemStyle: { color: '#0066FF' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0,102,255,.16)' },
              { offset: 1, color: 'rgba(0,102,255,0)' }
            ])
          }
        },
      ]
    }
  }

  /* 柱状图 */
  if (String(props.type) === '3') {
    return {
      ...baseConfig,
      title: {
        ...baseConfig.title,
        text: props.title,
        subtext: props.subTitle
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLabel: { color: '#64748B', formatter: formatLongText },
        axisLine: { lineStyle: { color: '#E2E8F0' } }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#94A3B8' },
        splitLine: { lineStyle: { color: '#E5EDFF' } }
      },
      series: [
        {
          type: 'bar',
          data: yData,
          barWidth: '38%',
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#3B82F6' },
              { offset: 1, color: '#93C5FD' }
            ])
          },
          label: {
            show: true,
            position: 'top',
            color: '#2563EB',
            fontSize: 12,
            fontWeight: 600
          }
        }
      ]
    }
  }

  /* 饼图 */
  if (String(props.type) === '4') {
    return {
      ...baseConfig,
      title: {
        ...baseConfig.title,
        text: props.title,
        subtext: props.subTitle
      },
      color: colors,
      tooltip: {
        trigger: 'item',
        formatter: ({ name, value, percent }) =>
        `${name}<br/><b>${value}</b>（${percent}%）`
      },
      legend: {
        orient: 'vertical',
        right: 36,         
        top: 'center',
        itemGap: 18,   
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: '#334155',
          fontSize: 16,   
          fontWeight: 500,
          lineHeight: 20,
          rich: {
            label: {
              padding: [0, 0, 0, -6]  // ★ 文字向左挪（关键点）
            }
          }
        },
        /* 左移“文字相对色块”的关键 */
        formatter: (name) => {
          return `{label|${name}}`
        },
      },
      series: [
        {
          type: 'pie',
          center: ['34%', '52%'],   // 左移
          radius: ['54%', '78%'],   // 减少空白
          /* 扇区质感 */
          itemStyle: {
            borderRadius: 10,
            borderColor: '#FFFFFF',
            borderWidth: 2,
            shadowBlur: 8,
            shadowColor: 'rgba(0,0,0,0.04)'
          },
          label: { show: false },
          emphasis: {
            scale: true,
            scaleSize: 6,
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 600,
              color: '#1E293B',
              formatter: '{d}%'
            }
          },
          data: xData.map((k, i) => ({
            name: k,
            value: yData[i]
          }))
        }
      ]
    }
  }

  return null
}

const renderChart = async () => {
  await nextTick()
  if (!chartRef.value) return
  if (!['2', '3', '4'].includes(String(props.type))) return

  // 先销毁旧实例
  if (myChart) {
    myChart.dispose()
    myChart = null
  }

  // 延迟到 DOM 真正有尺寸
  requestAnimationFrame(() => {
    myChart = echarts.init(chartRef.value)
    const option = getOption()
    option && myChart.setOption(option, true)
  })
}

watch([() => props.chartData, () => props.type], renderChart, { deep: true })

onMounted(() => {
  renderChart()
  resizeObserver = new ResizeObserver(() => myChart?.resize())
  chartRef.value && resizeObserver.observe(chartRef.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  myChart?.dispose()
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  margin: 15px 0;
}

/* AI 数据输出 */
.table-wrapper {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  max-height: 460px;
  overflow: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,.04);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  color: #334155;
}

.data-table th {
  background: #F8FAFC;
  color: #1E293B;
  padding: 16px 14px;
  text-align: left;
  font-weight: 700;
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 2px solid #E2E8F0;
}

.data-table td {
  padding: 14px;
  border-bottom: 1px solid #F1F5F9;
  white-space: nowrap;
  transition: background .2s, color .2s;
}

.data-table tr:hover td {
  background: #F0F7FF;
  color: #2563EB;
}

.echarts-container-box {
  width: 100%;
  height: 420px;
  padding: 22px;
  background: linear-gradient(
    180deg,
    #F8FAFF 0%,
    #FFFFFF 60%
  );
  border-radius: 18px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 14px 40px rgba(0,0,0,.05);
}

.echarts-wrapper {
  width: 100%;
  height: 100%;
}

.html-content {
  font-size: 14px;
  line-height: 1.8;
  color: #475569;
  background: linear-gradient(180deg,#F8FBFF,#FFFFFF);
  padding: 18px 20px;
  border-left: 3px solid #0066FF;
  border-radius: 0 12px 12px 0;
}

.table-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.table-wrapper::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 10px;
}
.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}
</style>
